import React, { useEffect, useState } from "react";
import { X, Download, Share } from "lucide-react";

/**
 * PWA install banner — shown only on the public Home (Landing) page.
 *
 * Two flavours:
 *   1. Chrome / Android — uses the standard `beforeinstallprompt` event so
 *      the install button triggers the native prompt.
 *   2. iOS Safari — there is no `beforeinstallprompt` API on iOS, so we show
 *      a tooltip-style banner instructing the user to use the share sheet.
 *
 * Constraints:
 *   • Only renders on mobile (window.innerWidth < 768).
 *   • Waits 30 seconds after page load before appearing (`SHOW_DELAY_MS`).
 *   • If the user dismisses, hides for 7 days via localStorage.
 *   • Hidden if the app is already running standalone (already installed).
 */

const STORAGE_KEY = "cartiq_pwa_dismissed_at";
const COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const SHOW_DELAY_MS = 30 * 1000; // 30 seconds
const MOBILE_MAX_WIDTH = 768;

const isStandalone = () => {
  if (typeof window === "undefined") return false;
  // Chrome / Android — display-mode media query
  if (window.matchMedia?.("(display-mode: standalone)").matches) return true;
  // iOS Safari — non-standard navigator.standalone
  if (window.navigator && "standalone" in window.navigator) {
    return Boolean(window.navigator.standalone);
  }
  return false;
};

const isIOSSafari = () => {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  // iPad on iOS 13+ reports as Mac, so also check touch points.
  const isIOS =
    /iPad|iPhone|iPod/.test(ua) ||
    (ua.includes("Mac") && navigator.maxTouchPoints > 1);
  // Treat Chrome / Edge / Firefox on iOS the same as Safari since none of
  // them expose beforeinstallprompt — all need the share-sheet hint.
  return isIOS;
};

const isCooldownActive = () => {
  try {
    const ts = parseInt(localStorage.getItem(STORAGE_KEY) || "0", 10);
    if (!ts) return false;
    return Date.now() - ts < COOLDOWN_MS;
  } catch {
    return false;
  }
};

export default function PWAInstallBanner() {
  // Phase the visibility logic so we don't flash the banner on slow devices.
  // mode: null | "android" | "ios"
  const [mode, setMode] = useState(null);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (isStandalone()) return; // already installed
    if (isCooldownActive()) return;
    if (window.innerWidth >= MOBILE_MAX_WIDTH) return;

    let cancelled = false;
    let timer = null;

    // Listen for the Chrome/Android beforeinstallprompt event. Stash the
    // event so we can fire the prompt on user click.
    const onBeforeInstall = (e) => {
      e.preventDefault();
      if (cancelled) return;
      setDeferredPrompt(e);
      setMode("android");
      // Defer visibility until 30s have elapsed.
      timer = setTimeout(() => {
        if (!cancelled) setVisible(true);
      }, SHOW_DELAY_MS);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // For iOS Safari: no native prompt, so just schedule the share-sheet
    // hint banner. Skip if we're not on iOS at all.
    if (isIOSSafari()) {
      timer = setTimeout(() => {
        if (!cancelled) {
          setMode("ios");
          setVisible(true);
        }
      }, SHOW_DELAY_MS);
    }

    return () => {
      cancelled = true;
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      if (timer) clearTimeout(timer);
    };
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch {
      /* ignore */
    }
    setVisible(false);
  };

  const install = async () => {
    if (!deferredPrompt) return;
    try {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
    } catch {
      /* ignore */
    } finally {
      setDeferredPrompt(null);
      // Either accepted or rejected — either way we shouldn't pester again.
      try {
        localStorage.setItem(STORAGE_KEY, String(Date.now()));
      } catch {
        /* ignore */
      }
      setVisible(false);
    }
  };

  if (!visible || !mode) return null;

  // ─── Android / Chrome variant ───────────────────────────────────────────
  if (mode === "android") {
    return (
      <div
        className="fixed left-0 right-0 z-50 px-3"
        style={{ bottom: "calc(64px + env(safe-area-inset-bottom))" }}
        data-testid="pwa-install-banner"
      >
        <div
          className="mx-auto max-w-md flex items-center gap-3 px-4 py-3 shadow-lg"
          style={{ background: "#FF6B00", color: "#0A0A0F" }}
        >
          <Download className="w-5 h-5 flex-shrink-0" strokeWidth={2.4} />
          <div className="flex-1 min-w-0 text-[12px] leading-tight font-semibold">
            Add CartIQ 360 to your home screen for instant access every week
          </div>
          <button
            type="button"
            onClick={install}
            className="flex-shrink-0 text-[10px] uppercase tracking-[0.14em] font-bold px-3 py-2 bg-[#0A0A0F] text-[#FF6B00] hover:bg-[#1A1A1A] transition-colors"
            data-testid="pwa-install-btn"
          >
            Add
          </button>
          <button
            type="button"
            onClick={dismiss}
            aria-label="Dismiss install banner"
            className="flex-shrink-0 text-[#0A0A0F]/70 hover:text-[#0A0A0F]"
            data-testid="pwa-dismiss-btn"
          >
            <X className="w-4 h-4" strokeWidth={2.4} />
          </button>
        </div>
      </div>
    );
  }

  // ─── iOS Safari variant — share-sheet instructions ──────────────────────
  return (
    <div
      className="fixed left-0 right-0 z-50 px-3"
      style={{ bottom: "calc(64px + env(safe-area-inset-bottom))" }}
      data-testid="pwa-ios-banner"
    >
      <div
        className="mx-auto max-w-md flex items-center gap-3 px-4 py-3 shadow-lg"
        style={{ background: "#FF6B00", color: "#0A0A0F" }}
      >
        <Share className="w-5 h-5 flex-shrink-0" strokeWidth={2.4} />
        <div className="flex-1 min-w-0 text-[12px] leading-tight font-semibold">
          Tap the share button then{" "}
          <span className="underline">&lsquo;Add to Home Screen&rsquo;</span> to
          install CartIQ 360
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss install banner"
          className="flex-shrink-0 text-[#0A0A0F]/70 hover:text-[#0A0A0F]"
          data-testid="pwa-ios-dismiss-btn"
        >
          <X className="w-4 h-4" strokeWidth={2.4} />
        </button>
      </div>
    </div>
  );
}
